import React, { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { STAGES } from '../workflow/EngagementProgressBoard';
import EngagementStageBadge from '../workflow/EngagementStageBadge';
import { STAGE_META } from '../workflow/constants';
import { ProvenanceBadge, provenanceOf } from '../shared/provenance';
import { DetailGrid, DetailItem } from '../workflow/SectionCard';
import {
  buildEngagementSections, EngagementSectionsView, EstimatedFeeCell, NasFeeCell, SECTION_TIMELINE, SECTION_CWCC,
} from '../shared/EngagementView';
import EngagementAttachments from '../shared/EngagementAttachments';
import NowName from '../shared/NowName';
import { serviceLabel } from '../../utils/engagementName';
import { formatDate } from '../../utils/dateFormat';
import { fetchJSON } from '../../utils/api';
import { natureLabel } from '../../pages/qcAddendum';

// This engagement's move in an addendum: its own line when the addendum
// revised the record's engagements one by one (null = not this one).
const ownRiskChange = (a, e) => {
  const changes = a.riskChanges || [];
  if (changes.length === 0) return <> · Risk <span className="font-medium text-amber-700">{a.riskFrom || '—'} → {a.riskTo}</span></>;
  const own = changes.find((c) => String(c.engagementId) === String(e.id));
  return own ? <> · Risk <span className="font-medium text-amber-700">{own.from || '—'} → {own.to}</span></> : null;
};

/**
 * A client's engagements, year by year (docs/modules/client-hub.md §3).
 *
 * One card per engagement, newest FYE first. The collapsed card is the
 * year's headline — FYE, service, status, the five stage chips, who ran it,
 * the QC6/QC7 verdict (the file reference lives in the expanded card's
 * Engagement block) — with an amber dot on any headline field that differs
 * from the year before, so a decade of audits reads as "what changed when"
 * instead of ten identical rows. Expanding a card shows the engagement's
 * sections (the same builder the management view uses, minus the Signed Financial Statements /
 * QC35 timeline and CaseWare & Charge Code) and the remarks. The QC summary
 * and the attachment list are built but held back (SHOW_QC_AND_FILES).
 *
 * Nothing here links to a request: the hub is summary-only by the owner's
 * call, so request numbers print as text.
 */

const RISK_CLS = {
  Low: 'bg-green-50 text-green-700 border-green-200',
  Moderate: 'bg-amber-50 text-amber-700 border-amber-200',
  High: 'bg-red-50 text-red-700 border-red-200',
};

/**
 * Where a rating comes from, in one line — the acceptance (type · request ·
 * approval date) and, when an addendum revised it, that addendum and its date.
 * `src` is the `risk` object on a QC summary or the profile's
 * `currentRiskSource`; both carry the same keys.
 */
export const riskProvenance = (src, { qcType = null, sourceRequestNo = null, source = null, legacyImport = null } = {}) => {
  if (!src) return null;
  const type = qcType || src.qcType;
  const req = sourceRequestNo || src.sourceRequestNo;
  const imported = legacyImport === null ? src.legacyImport === true : legacyImport === true;
  const kind = provenanceOf(source || src.source);
  // Same three-way split as ProvenanceBadge: the data-import tool, a signed
  // form uploaded in Management (its date is the approval date typed there,
  // not a workflow approval), plain Management, or a workflow request.
  const origin = req || (kind === 'import' ? 'imported'
    : imported ? 'imported signed form (Management)'
      : kind === 'manual' ? 'recorded in Management' : 'workflow');
  const rated = `${type || 'QC6/QC7'} · ${origin}${src.ratedOn ? ` · approved ${formatDate(src.ratedOn)}` : ''}`;
  return { rated, revised: riskRevisedText(src) };
};

// "Revised from X by QCAD-n on …" — null when no addendum revised the rating.
export const riskRevisedText = (src) => (src && src.revisedBy
  ? `Revised ${src.original ? `from ${src.original} ` : ''}by ${src.revisedBy}${src.revisedOn ? ` on ${formatDate(src.revisedOn)}` : ''}`
  : null);

export const RiskChip = ({ rating, className = '' }) => (
  rating
    ? <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${RISK_CLS[rating] || 'bg-slate-100 text-slate-600 border-slate-200'} ${className}`}>{rating}</span>
    : <span className="text-slate-400">—</span>
);

// The headline fields compared against the previous year (older engagement).
// The people fields carry a fourth reader: the staff FK. Names are snapshots
// (docs/data-model.md §2), so a partner who was renamed between the two years
// reads as two different strings while being the same person — whenever both
// years resolve to a staff row the comparison goes through the FK instead.
const TRACKED = [
  ['partner', 'Partner', (e) => e.partner || null, (e) => e.partnerStaffId || null],
  ['director', 'Director',
    (e) => ((e.directors || [])[0] || {}).staffName || e.director || null,
    (e) => ((e.directors || [])[0] || {}).staffId || e.directorStaffId || null],
  ['manager', 'Manager',
    (e) => ((e.managers || [])[0] || {}).staffName || e.manager || null,
    (e) => ((e.managers || [])[0] || {}).staffId || e.managerStaffId || null],
  ['service', 'Service', (e) => e.serviceProvided || null],
  ['risk', 'Risk Rating', (e) => (e.qc && e.qc.risk ? e.qc.risk.rating : null)],
  ['eqr', 'EQR', (e) => e.eqr || null, (e) => e.eqrStaffId || null],
];

/**
 * For each engagement (newest first) the fields that differ from the prior
 * year's engagement OF THE SAME SERVICE — a company may hold an audit and an
 * AUP engagement for one FYE, and comparing one against the other would flag
 * every field.
 */
export const yearOverYearChanges = (engagements) => {
  const byOldest = [...engagements].sort((a, b) => String(a.yearEnd).localeCompare(String(b.yearEnd)) || a.id - b.id);
  const out = new Map();
  const svcOf = (e) => String(e.serviceProvided || '').toLowerCase();
  byOldest.forEach((e, i) => {
    let prev = null;
    for (let j = i - 1; j >= 0; j -= 1) {
      if (svcOf(byOldest[j]) === svcOf(e) && String(byOldest[j].yearEnd) < String(e.yearEnd)) { prev = byOldest[j]; break; }
    }
    const changes = {};
    if (prev) {
      TRACKED.forEach(([key, , read, idOf]) => {
        const now = read(e);
        const before = read(prev);
        const idNow = idOf ? idOf(e) : null;
        const idBefore = idOf ? idOf(prev) : null;
        const same = (idNow && idBefore)
          ? String(idNow) === String(idBefore)
          : now === before;
        // A field this year hasn't filled yet (risk/EQR before QC6/QC7) isn't a change.
        if (!same && now) changes[key] = { before, prevFye: prev.yearEnd };
      });
    }
    out.set(e.id, changes);
  });
  return out;
};

const fyeYear = (d) => String(d || '').slice(0, 4);

// A headline cell: label above, value below, amber dot when it changed since
// the year before (title says what it was).
// `wrap` lets a cell run to a second line (the risk cell carries a date).
const Head = ({ label, change, wrap = false, children }) => (
  <div className="min-w-0">
    <p className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
    <div className={`text-sm text-slate-900 flex gap-1.5 ${wrap ? 'items-start' : 'items-center truncate'}`}>
      {change && (
        <span
          className={`w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 ${wrap ? 'mt-2' : ''}`}
          title={`Changed — was ${change.before || '—'} (FYE ${fyeYear(change.prevFye)})`}
          aria-label="changed since the previous year"
        />
      )}
      <span className={wrap ? 'min-w-0' : 'truncate'}>{children}</span>
    </div>
  </div>
);

const tri = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

// ── QC6/QC7 summary (type, date, rating, EQR, addenda) — no form, no PDF ──
const QcSummary = ({ engagement: e }) => {
  const qc = e.qc;
  if (!qc) {
    return (
      <p className="text-sm text-slate-500">No QC6 / QC7 form recorded for this engagement yet.</p>
    );
  }
  const addenda = qc.addenda || [];
  return (
    <div className="space-y-4">
      <DetailGrid cols={3}>
        <DetailItem label="Form">{qc.qcType}</DetailItem>
        <DetailItem label="Approved On">{qc.approvedOn ? formatDate(qc.approvedOn) : '—'}</DetailItem>
        <DetailItem label="Source">
          {provenanceOf(qc.source) === 'workflow'
            ? (qc.sourceRequestNo || 'Workflow')
            : <ProvenanceBadge source={qc.source} />}
        </DetailItem>
        <DetailItem label="Risk Rating (Current)">
          <RiskChip rating={qc.risk.rating} />
          {riskRevisedText(qc.risk) && (
            <span className="block text-xs font-normal text-amber-700 mt-1">{riskRevisedText(qc.risk)}</span>
          )}
        </DetailItem>
        <DetailItem label="EQR">{e.eqr
          ? <NowName value={e.eqr} now={e.eqrNow} />
          : (qc.eqr || '—')}</DetailItem>
        <DetailItem label="TNA / TNE">{tri(e.isTna)} / {tri(e.isTne)}</DetailItem>
      </DetailGrid>

      <div>
        <p className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
          Addenda {addenda.length > 0 && <span className="normal-case font-normal">· {addenda.length}</span>}
        </p>
        {addenda.length === 0 ? (
          <p className="text-sm text-slate-500">No addendum filed.</p>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {addenda.map((a, i) => (
              <li key={a.id} className={`px-3 py-2 text-sm ${i === 0 ? 'bg-blue-50/40' : ''}`}>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="font-medium text-slate-900">Addendum to Form {qc.qcType}</span>
                  <span className="text-slate-500">· {a.approvedOn ? formatDate(a.approvedOn) : 'no date'}</span>
                  {i === 0 && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700">Latest</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {(a.nature || []).map(natureLabel).join(', ') || 'Nature not recorded'}
                  {a.riskChanged
                    ? (ownRiskChange(a, e) || ' · Risk unchanged for this engagement')
                    : ' · Risk unchanged'}
                  {' · '}
                  {a.sourceRequestNo ? `via ${a.sourceRequestNo}` : 'recorded in Management'}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// The QC6/QC7 Form and Attachments blocks of an expanded card — hidden for
// now, may be reopened later.
const SHOW_QC_AND_FILES = false;

// ── Attachments (hub-filtered list; names only — the hub serves no file) ───
const HubAttachments = ({ basePath }) => {
  const [files, setFiles] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(`${basePath}/attachments`);
        if (!cancelled) setFiles(data.data || []);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [basePath]);
  if (error) {
    return (
      <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
        {error}
      </div>
    );
  }
  if (files === null) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <EngagementAttachments attachments={files} />
  );
};

// Sub-block inside an expanded card.
const Block = ({ title, children, collapsible = false }) => {
  const [open, setOpen] = useState(!collapsible);
  return (
    <div className="border border-slate-200 rounded-lg">
      <button
        type="button"
        onClick={() => collapsible && setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50 rounded-t-lg text-left ${collapsible ? '' : 'cursor-default'}`}
      >
        {collapsible && <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} />}
        <span className="text-sm font-medium text-slate-700">{title}</span>
      </button>
      {open && <div className="p-4 border-t border-slate-200">{children}</div>}
    </div>
  );
};

// ── One year (card) ────────────────────────────────────────────────────────
const YearCard = ({ engagement: e, changes, open, onToggle }) => {
  const basePath = `/api/client-hub/engagements/${e.id}`;
  const sourceCell = provenanceOf(e.source) === 'workflow' ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
      {e.sourceRequestNo || 'Workflow'}
    </span>
  ) : <ProvenanceBadge source={e.source} />;
  // Same rule as the management view; the QC figure comes with the summary.
  const estimatedFeeCell = (
    <EstimatedFeeCell
      engagement={e}
      qcFee={e.qc ? e.qc.estimatedFee : null}
      qcType={e.qc && e.qc.qcType}
      qcSourceRequestNo={e.qc && e.qc.sourceRequestNo}
    />
  );
  const nasFeeCell = (
    <NasFeeCell
      nasFee={e.qc ? e.qc.nasFee : null}
      qcType={e.qc && e.qc.qcType}
      qcSourceRequestNo={e.qc && e.qc.sourceRequestNo}
    />
  );
  const directorLead = (e.directors || [])[0] || null;
  const director = directorLead ? directorLead.staffName : e.director;
  const directorNow = directorLead ? directorLead.staffNameNow : e.directorNow;
  const managerLead = (e.managers || [])[0] || null;
  const manager = managerLead ? managerLead.staffName : e.manager;
  const managerNow = managerLead ? managerLead.staffNameNow : e.managerNow;
  const addendaCount = e.qc ? (e.qc.addenda || []).length : 0;

  return (
    <div className={`border rounded-lg ${open ? 'border-blue-300 shadow-sm' : 'border-slate-200'} bg-white`}>
      <button type="button" onClick={onToggle} className="w-full text-left px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
          <span className="text-base font-semibold text-slate-900">FYE {formatDate(e.yearEnd)}</span>
          {/* 同 FYE 可多案：標題列就要講清楚是哪個服務（＋序號與描述第一行）。 */}
          <span className="text-sm text-slate-600">{serviceLabel(e.serviceProvided, e.engagementSeq) || '—'}</span>
          {e.serviceDescription && (
            <span className="text-xs text-slate-500 truncate max-w-[18rem]" title={e.serviceDescription}>{e.serviceDescription}</span>
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{e.status || '—'}</span>
          <EngagementStageBadge stage={e.currentStage} />
          {/* The five gates, named — the board's colours (STAGE_META), the
              gate's name as the text, the state in the tooltip. Signed Financial Statements /
              QC35 only ever arrive here as done / pending / none. */}
          <span className="ml-auto flex flex-wrap items-center gap-1">
            {STAGES.map(([key, label]) => {
              const meta = STAGE_META[e.stages[key]] || STAGE_META.none;
              return (
                <span
                  key={key}
                  title={`${label}: ${meta.hint || meta.label}`}
                  className={`inline-flex items-center whitespace-nowrap px-1.5 py-0.5 rounded text-[10.5px] font-medium ${meta.cls}`}
                >
                  {label}
                </span>
              );
            })}
          </span>
        </div>
        <div className="mt-2.5 pl-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-x-4 gap-y-2">
          <Head label="Service" change={changes.service}>{e.serviceProvided || '—'}</Head>
          <Head label="Partner" change={changes.partner}><NowName value={e.partner} now={e.partnerNow} /></Head>
          <Head label="Director" change={changes.director}><NowName value={director} now={directorNow} /></Head>
          <Head label="Manager" change={changes.manager}><NowName value={manager} now={managerNow} /></Head>
          <Head label="QC6 / QC7">
            {e.qc ? `${e.qc.qcType}${e.qc.approvedOn ? ` · ${formatDate(e.qc.approvedOn)}` : ''}` : '—'}
            {addendaCount > 0 && <span className="text-xs text-slate-500"> · {addendaCount} addend{addendaCount === 1 ? 'um' : 'a'}</span>}
          </Head>
          <Head label="Risk Rating" change={changes.risk} wrap>
            <RiskChip rating={e.qc && e.qc.risk ? e.qc.risk.rating : null} />
            {/* "as of" = the addendum's date when revised, else the acceptance's —
                on its own line, the full provenance in the tooltip. */}
            {e.qc && e.qc.risk && e.qc.risk.asOf && (
              <span className="block text-[11px] leading-snug text-slate-500 mt-0.5" title={riskProvenance(e.qc.risk, { qcType: e.qc.qcType, sourceRequestNo: e.qc.sourceRequestNo, source: e.qc.source, legacyImport: e.qc.legacyImport }).rated}>
                {e.qc.risk.revisedBy ? `${e.qc.risk.revisedBy} · ` : ''}{formatDate(e.qc.risk.asOf)}
              </span>
            )}
          </Head>
          <Head label="EQR" change={changes.eqr}><NowName value={e.eqr} now={e.eqrNow} /></Head>
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-200 p-4 space-y-4">
          <EngagementSectionsView
            sections={buildEngagementSections(e, { estimatedFeeCell, nasFeeCell, sourceCell, omit: [SECTION_TIMELINE, SECTION_CWCC] })}
            teamMembers={e.teamMembers}
          />
          <div className="border-t border-slate-100 pt-3">
            <DetailItem label="Remarks">{e.remarks || '—'}</DetailItem>
          </div>
          {SHOW_QC_AND_FILES && (
            <>
              <Block title="QC6 / QC7 Form">
                <QcSummary engagement={e} />
              </Block>
              <Block title="Attachments">
                <HubAttachments basePath={basePath} />
              </Block>
            </>
          )}
          {/* 沒有 History：Hub 只給現況（歷程的 diff 帶著 Signed Financial Statements／QC35 的
              日期與連結，不在 Hub 的範圍內）。 */}
        </div>
      )}
    </div>
  );
};

/**
 * @param engagements rows from GET /api/client-hub/entities/:id (newest first)
 */
const ClientEngagementTimeline = ({ engagements = [] }) => {
  const [openId, setOpenId] = useState(null);
  const changes = yearOverYearChanges(engagements);

  if (engagements.length === 0) {
    return <p className="text-sm text-slate-500">No engagement recorded for this company.</p>;
  }

  // Risk trajectory — oldest to newest, one chip per QC record. Several
  // engagements of one FYE (audit + AUPs) share a single QC6/QC7, so the
  // record, not the engagement, is the unit; only a year with two distinct
  // records shows two chips.
  const seenQc = new Set();
  const rated = [...engagements]
    .filter((e) => e.qc && e.qc.risk && e.qc.risk.rating)
    .sort((a, b) => String(a.yearEnd).localeCompare(String(b.yearEnd)))
    .filter((e) => (seenQc.has(e.qc.id) ? false : (seenQc.add(e.qc.id), true)));

  return (
    <div className="space-y-4">
      {rated.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide mr-1">Risk Rating by Year</span>
          {rated.map((e) => (
            <span key={e.id} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span>{fyeYear(e.yearEnd)}</span>
              <RiskChip rating={e.qc.risk.rating} />
            </span>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-500">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 align-middle mr-1.5" />
        marks a field that changed from the previous year. Newest first; click a year to expand it.
      </p>
      <div className="space-y-3">
        {engagements.map((e) => (
          <YearCard
            key={e.id}
            engagement={e}
            changes={changes.get(e.id) || {}}
            open={openId === e.id}
            onToggle={() => setOpenId((v) => (v === e.id ? null : e.id))}
          />
        ))}
      </div>
    </div>
  );
};

export default ClientEngagementTimeline;
